// src/pages/PrivacyPolicy.jsx
import React, { useEffect } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  UnorderedList,
  ListItem,
  Divider,
  useColorModeValue
} from '@chakra-ui/react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const PrivacyPolicy = () => {
  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Header />
      <Box pt="100px" pb="60px" minH="100vh" bg={useColorModeValue('gray.50', 'gray.900')}>
        <Container maxW="1000px">
          <VStack spacing={8} align="stretch">
            <Heading 
              as="h1" 
              fontSize={{ base: '2xl', md: '3xl', lg: '4xl' }}
              textAlign="center"
              bgGradient="linear(to-r, brand.primary, brand.secondary)"
              bgClip="text"
              mb={8}
            >
              Política de Privacidade
            </Heading>

            <Box 
              bg="white" 
              p={8} 
              borderRadius="lg" 
              boxShadow="md"
            >
              <Text color="gray.700" mb={4}>
                Última atualização: 07 de Maio de 2025
              </Text>

              <Text color="gray.700" mb={6}>
                A Draw Your Goods valoriza a sua privacidade e está comprometida em proteger seus dados pessoais. Esta Política de Privacidade descreve como coletamos, usamos e compartilhamos suas informações quando você utiliza nosso site, aplicativo ou serviços.
              </Text>

              <Divider my={6} />

              <VStack spacing={8} align="stretch">
                <Box>
                  <Heading as="h2" size="md" color="brand.primary" mb={4}>
                    1. Informações que Coletamos
                  </Heading>

                  <Text color="gray.700" mb={4}>
                    Coletamos os seguintes tipos de informações:
                  </Text>

                  <UnorderedList spacing={2} pl={4} color="gray.700">
                    <ListItem>
                      <Text fontWeight="medium">Informações de cadastro:</Text> Nome, endereço de e-mail, senha e outros dados necessários para criar e gerenciar sua conta.
                    </ListItem>
                    <ListItem>
                      <Text fontWeight="medium">Informações de pagamento:</Text> Dados de pagamento necessários para processar suas compras (processados de forma segura por nossos parceiros de pagamento).
                    </ListItem>
                    <ListItem>
                      <Text fontWeight="medium">Fotos e conteúdo:</Text> Imagens que você faz upload para transformação em desenhos para colorir.
                    </ListItem>
                    <ListItem>
                      <Text fontWeight="medium">Informações de uso:</Text> Dados sobre como você interage com nosso site, incluindo páginas visitadas, recursos utilizados e tempo gasto.
                    </ListItem>
                    <ListItem>
                      <Text fontWeight="medium">Informações do dispositivo:</Text> Tipo de dispositivo, sistema operacional, navegador e endereço IP.
                    </ListItem>
                  </UnorderedList>
                </Box>

                <Box>
                  <Heading as="h2" size="md" color="brand.primary" mb={4}>
                    2. Como Usamos Suas Informações
                  </Heading>

                  <Text color="gray.700" mb={4}>
                    Utilizamos suas informações para:
                  </Text>

                  <UnorderedList spacing={2} pl={4} color="gray.700">
                    <ListItem>Fornecer, manter e melhorar nossos serviços</ListItem>
                    <ListItem>Processar e gerenciar suas compras de créditos</ListItem>
                    <ListItem>Transformar suas fotos em desenhos para colorir</ListItem>
                    <ListItem>Comunicar atualizações, ofertas e informações relevantes (com sua permissão)</ListItem>
                    <ListItem>Personalizar sua experiência em nossa plataforma</ListItem>
                    <ListItem>Proteger contra atividades fraudulentas e não autorizadas</ListItem>
                    <ListItem>Cumprir obrigações legais</ListItem>
                  </UnorderedList>
                </Box>

                <Box>
                  <Heading as="h2" size="md" color="brand.primary" mb={4}>
                    3. Compartilhamento de Informações
                  </Heading>

                  <Text color="gray.700" mb={4}>
                    Compartilhamos suas informações apenas nas seguintes circunstâncias:
                  </Text>

                  <UnorderedList spacing={2} pl={4} color="gray.700">
                    <ListItem>
                      <Text fontWeight="medium">Provedores de serviços:</Text> Trabalhamos com empresas que nos ajudam a operar, manter e melhorar nossos serviços, como processadores de pagamento, serviços de hospedagem e serviços de análise.
                    </ListItem>
                    <ListItem>
                      <Text fontWeight="medium">Requisitos legais:</Text> Podemos divulgar informações quando acreditamos, de boa-fé, que a divulgação é necessária para cumprir a lei, proteger nossos direitos ou investigar fraudes.
                    </ListItem>
                    <ListItem>
                      <Text fontWeight="medium">Com seu consentimento:</Text> Compartilharemos suas informações pessoais para outros fins com seu consentimento explícito.
                    </ListItem>
                  </UnorderedList>

                  <Text color="gray.700" mt={4} fontWeight="medium">
                    Nunca vendemos seus dados pessoais a terceiros.
                  </Text>
                </Box>

                <Box>
                  <Heading as="h2" size="md" color="brand.primary" mb={4}>
                    4. Retenção de Dados
                  </Heading>

                  <Text color="gray.700">
                    Mantemos suas informações pessoais pelo tempo necessário para fornecer os serviços solicitados e cumprir nossas obrigações legais. As fotos que você faz upload são armazenadas em nossa plataforma para permitir que você acesse seus desenhos em sua galeria pessoal, mas você pode solicitar a exclusão a qualquer momento.
                  </Text>
                </Box>

                <Box>
                  <Heading as="h2" size="md" color="brand.primary" mb={4}>
                    5. Seus Direitos
                  </Heading>

                  <Text color="gray.700" mb={4}>
                    De acordo com a Lei Geral de Proteção de Dados (LGPD), você tem os seguintes direitos:
                  </Text>

                  <UnorderedList spacing={2} pl={4} color="gray.700">
                    <ListItem>Acessar e corrigir seus dados pessoais</ListItem>
                    <ListItem>Solicitar a exclusão de seus dados (direito ao esquecimento)</ListItem>
                    <ListItem>Solicitar limitação do processamento de seus dados</ListItem>
                    <ListItem>Revogar consentimento previamente fornecido</ListItem>
                    <ListItem>Solicitar a portabilidade de seus dados</ListItem>
                  </UnorderedList>

                  <Text color="gray.700" mt={4}>
                    Para exercer esses direitos, entre em contato conosco através do e-mail: drawgoodscontato@gmail.com
                  </Text>
                </Box>

                <Box>
                  <Heading as="h2" size="md" color="brand.primary" mb={4}>
                    6. Segurança de Dados
                  </Heading>

                  <Text color="gray.700">
                    Implementamos medidas técnicas e organizacionais apropriadas para proteger seus dados pessoais contra acesso não autorizado, perda acidental ou alteração. Utilizamos criptografia, firewalls, controles de acesso e outras práticas de segurança para proteger suas informações.
                  </Text>
                </Box>

                <Box>
                  <Heading as="h2" size="md" color="brand.primary" mb={4}>
                    7. Cookies e Tecnologias Semelhantes
                  </Heading>

                  <Text color="gray.700">
                    Utilizamos cookies e tecnologias semelhantes para melhorar sua experiência em nosso site, lembrar suas preferências e entender como nossos usuários navegam pela plataforma. Você pode gerenciar suas preferências de cookies através das configurações do seu navegador.
                  </Text>
                </Box>

                <Box>
                  <Heading as="h2" size="md" color="brand.primary" mb={4}>
                    8. Alterações na Política de Privacidade
                  </Heading>

                  <Text color="gray.700">
                    Podemos atualizar esta Política de Privacidade periodicamente para refletir mudanças em nossas práticas de privacidade. Notificaremos você sobre quaisquer alterações significativas através de um aviso em nosso site ou por e-mail, e indicaremos a data da última atualização no topo desta política.
                  </Text>
                </Box>

                <Box>
                  <Heading as="h2" size="md" color="brand.primary" mb={4}>
                    9. Contato
                  </Heading>

                  <Text color="gray.700">
                    Se você tiver dúvidas, preocupações ou solicitações relacionadas a esta Política de Privacidade ou ao tratamento de seus dados pessoais, entre em contato conosco através do e-mail: drawgoodscontato@gmail.com
                  </Text>
                </Box>
              </VStack>
            </Box>
          </VStack>

          {/* SEO - Schema.org markup for Google */}
          <script type="application/ld+json">{`
            {
              "@context": "https://schema.org",
              "@type": "WebPage",
              "name": "Política de Privacidade - Draw Your Goods",
              "description": "Nossa política de privacidade explica como coletamos, usamos e protegemos seus dados pessoais.",
              "publisher": {
                "@type": "Organization",
                "name": "Draw Your Goods",
                "logo": "https://www.drawyourgoods.com.br/logo.png"
              },
              "datePublished": "2025-05-07",
              "dateModified": "2025-05-07"
            }
          `}</script>
        </Container>
      </Box>
      <Footer />
    </>
  );
};

export default PrivacyPolicy;